"use strict";

import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useAlertCount } from "@/api/alerts";
import { useChats } from "@/api/chats";
import { usePremiumFeatures } from "@/api/features";
import { useUserStream } from "@/api/hooks";
import { usePendingPolicy } from "@/api/policies";
import { useRegisterPushNotifications } from "@/api/push-notifications";
import { supportsPushNotifications } from "@/api/push-notifications/utils";
import SidebarNavigation from "@/components/sidebar/sidebar-navigation";
import ThumbNavigation from "@/components/thumb-navigation";
import { Layout } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useFullScreenContext } from "@/contexts/full-screen-context";
import { useModalContext } from "@/contexts/modal-context";
import { useDraggedFiles, useOnboarding } from "@/hooks";
import { useGeneralHotkeys } from "@/hooks/useGeneralHotkeys";
import { useMediaDeepLink } from "@/hooks/useMediaDeepLink";
import { useImpressionRouteFlush } from "@/hooks/useStatusTracking";
import { cn } from "@/utils/cn";

import FloatingActionButton from "./components/floating-action-button";
import { supportedPolicyIds } from "./components/modals/policy-modal";
import Navbar from "./components/navbar";
// Dummy import, to make sure that <Status /> ends up in the application bundle.
// Without this it ends up in ~8 very commonly used bundles.
import "@/components/status";
import { ChatWidget, SidebarMenu } from "./util/async-components";

const NOTIFICATION_PERMISSION_DELAY_MS = 120 * 1000;

const UI: React.FC = () => {
  const { openModal } = useModalContext();
  const { data: pendingPolicy } = usePendingPolicy();
  const node = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const handledShareCompose = useRef(false);

  const { isLoggedIn } = useAuth();
  const { isFullScreen } = useFullScreenContext();

  useRegisterPushNotifications();

  const { checkOnboardingStatus } = useOnboarding();

  const showOnboarding = useMemo(() => {
    const needsOnboarding = checkOnboardingStatus();
    return isLoggedIn && needsOnboarding;
  }, [isLoggedIn]);

  /** Fetch global alerts categories and chats state */
  void useAlertCount();
  void useChats();
  void usePremiumFeatures();

  // Flush queued impression events on route change.
  useImpressionRouteFlush();

  const { isDragging } = useDraggedFiles(node);

  const handleDragEnter = (e: DragEvent) => e.preventDefault();
  const handleDragLeave = (e: DragEvent) => e.preventDefault();
  const handleDragOver = (e: DragEvent) => e.preventDefault();
  const handleDrop = (e: DragEvent) => e.preventDefault();

  useEffect(() => {
    if (
      supportsPushNotifications &&
      window.Notification?.permission === "default"
    ) {
      window.setTimeout(
        () => Notification.requestPermission(),
        NOTIFICATION_PERMISSION_DELAY_MS,
      );
    }
  }, []);

  useEffect(() => {
    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    if (showOnboarding) {
      openModal("ONBOARDING");
    }

    return () => {
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    };
  }, []);

  useUserStream();
  useGeneralHotkeys();
  useMediaDeepLink();

  useEffect(() => {
    if (
      isLoggedIn &&
      pendingPolicy &&
      supportedPolicyIds.includes(pendingPolicy.pending_policy_id)
    ) {
      setTimeout(() => {
        openModal("POLICY");
      }, 500);
    }
  }, [pendingPolicy, isLoggedIn]);

  useEffect(() => {
    const state = location.state as { openShareCompose?: boolean } | null;

    if (
      location.pathname === "/" &&
      state?.openShareCompose &&
      !handledShareCompose.current
    ) {
      handledShareCompose.current = true;
      openModal("COMPOSE", { composeId: "share-modal" });
    }
  }, [location.pathname, location.state]);

  const shouldHideFAB = (): boolean => {
    const path = location.pathname;
    return Boolean(
      path.match(
        /^\/posts\/|^\/search|^\/getting-started|^\/chats|^\/settings|^\/ai-search/,
      ),
    );
  };

  return (
    <div ref={node} className="min-h-full bg-white dark:bg-gray-900">
      <div
        className={cn(
          "pointer-events-none fixed z-90 h-screen w-screen transition",
          {
            "backdrop-blur-sm": isDragging,
          },
        )}
      />

      <div className="z-10 flex min-h-full flex-col">
        <Navbar />

        <Layout>
          {!isFullScreen && (
            <Layout.Sidebar>
              <SidebarNavigation />
            </Layout.Sidebar>
          )}

          <Outlet />
        </Layout>

        {isLoggedIn && !shouldHideFAB() && (
          <div className="fixed right-4 bottom-24 z-40 transition-all lg:hidden rtl:right-auto rtl:left-4">
            <FloatingActionButton />
          </div>
        )}

        {isLoggedIn && (
          <Suspense fallback={null}>
            <SidebarMenu />
          </Suspense>
        )}

        {isLoggedIn && (
          <Suspense fallback={null}>
            <div className="hidden xl:block">
              <ChatWidget />
            </div>
          </Suspense>
        )}
        <ThumbNavigation />
      </div>
    </div>
  );
};

export default UI;
